import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Image, Text, Box, useDisclosure } from '@stockx/ui';
import { trackEvent as segmentTrackEvent, EVENTS, SCREENS } from '@app/segment';
import { useGATrackEvent, Events, Categories } from '@app/analytics';
import useBuildContentstackImageSrc from '@drc/hooks/useBuildContentstackImageSrc';
import {
  StyledUserGeneratedContentTile,
  UserGeneratedContentTileShopNowButton,
} from './UserGeneratedContentTile.styles';
import { UGCTileProps } from '../../types';
import { UserGeneratedContentModal } from '../UserGeneratedContentModal';
import currentIndexState from '../../states/currentIndexState';

const UserGeneratedContentTile: React.FC<UGCTileProps> = ({
  title,
  creator,
  image,
  idx,
}) => {
  const { imageSrc, imageSrcSet } = useBuildContentstackImageSrc({
    src: image.url,
    options: {
      width: 296,
      height: 296,
      fit: 'crop',
    },
  });

  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [, setCurrentIndex] = useRecoilState(currentIndexState);
  const gaTrack = useGATrackEvent();

  const trackClick = () => {
    segmentTrackEvent(EVENTS.AS_SEEN_ON_INSTAGRAM_CLICKED, {
      screenName: SCREENS.HOME,
      position: `${idx}`,
    });
    gaTrack(Events.AsSeenOnInstagramClicked, Categories.Home, idx);
  };

  const onInstagramWindowClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  const handleUGCTileClick = (e) => {
    e.stopPropagation();
    setCurrentIndex(idx);
    trackClick();
    onOpen();
  };

  return (
    <StyledUserGeneratedContentTile>
      <Box onClick={handleUGCTileClick}>
        <UserGeneratedContentTileShopNowButton
          variant="secondary"
          size="lg"
          data-testid="ugc-shopnow-button"
          onClick={handleUGCTileClick}
        >
          <Text>{t('user_generated_content_collection_tile_shop_now')}</Text>
        </UserGeneratedContentTileShopNowButton>
        <Text fontSize="md">{creator}</Text>
      </Box>
      <Image
        referrerPolicy="origin"
        zIndex={1}
        w="100%"
        h="100%"
        objectFit="contain"
        alt={title}
        decoding="async"
        fetchpriority="low"
        srcSet={imageSrcSet}
        src={imageSrc}
      />
      {isOpen && (
        <UserGeneratedContentModal
          isOpen={isOpen}
          onClose={onInstagramWindowClose}
        />
      )}
    </StyledUserGeneratedContentTile>
  );
};

export default UserGeneratedContentTile;
